import React from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { isPrivilegedUser, isOwnTask, canEditTask, canDeleteTask, getTaskCreator } from "../../../Common/permissions";
import TaskDetails from "../TaskCalendar/TaskDetails";
import BulkUpload from "./BulkUpload";
import Create from "./Create";
import Edit from "./Edit";
import StatusUpdate from "./StatusUpdate";

const List = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [bulkDialogVisible, setBulkDialogVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("exclude_done");
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [developerFilter, setDeveloperFilter] = useState(null);
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [projectFilter, setProjectFilter] = useState(null);
  const [error, setError] = useState("");
  const [highlightedItems, setHighlightedItems] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const toast = useRef(null);
  const filterPanelRef = useRef(null);

  // Admins and department heads get full access; everyone else is restricted.
  const privileged = isPrivilegedUser(currentUser);
  const canManageTask = (task) =>
    privileged || (currentUser && isOwnTask(task, currentUser.id));

  // Default (neutral) filter values — used to count "active" filters and to reset.
  const STATUS_DEFAULT = "exclude_done";
  const SCHEDULE_DEFAULT = "all";

  const activeFilterCount =
    (statusFilter !== STATUS_DEFAULT ? 1 : 0) +
    (scheduleFilter !== SCHEDULE_DEFAULT ? 1 : 0) +
    (developerFilter ? 1 : 0) +
    (projectFilter ? 1 : 0);

  const clearFilters = () => {
    setStatusFilter(STATUS_DEFAULT);
    setScheduleFilter(SCHEDULE_DEFAULT);
    setDeveloperFilter(null);
    setProjectFilter(null);
  };

  // Function to highlight an item for 2 seconds
  const highlightItem = (itemId) => {
    setHighlightedItems((prev) => ({
      ...prev,
      [`task-${itemId}`]: true,
    }));

    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newState = { ...prev };
        delete newState[`task-${itemId}`];
        return newState;
      });
    }, 2000);
  };

  // Handle adding a new task without refreshing all data
  const handleAddNewTask = (newTaskData) => {
    if (!newTaskData) return;

    setTasks((prevTasks) => {
      const exists = prevTasks.some((task) => task.id === newTaskData.id);
      if (exists) return prevTasks;
      highlightItem(newTaskData.id);
      return [...prevTasks, newTaskData];
    });

    setCreateDialogVisible(false);

    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task created successfully",
      life: 3000,
    });
  };

  // Handle bulk-created tasks from the CSV upload modal.
  const handleBulkCreated = (newTasks) => {
    if (!Array.isArray(newTasks) || newTasks.length === 0) return;
    setTasks((prev) => {
      const existingIds = new Set(prev.map((t) => t.id));
      const additions = newTasks.filter((t) => t && t.id && !existingIds.has(t.id));
      additions.forEach((t) => highlightItem(t.id));
      return [...prev, ...additions];
    });
    setBulkDialogVisible(false);
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: `${newTasks.length} task(s) created successfully`,
      life: 3000,
    });
  };

  // Handle updating a task without refreshing all data
  const handleUpdateTask = (updatedTaskData) => {
    if (!updatedTaskData) return;

    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          const taskWithUpdates = {
            ...task,
            ...updatedTaskData,
            developers: Array.isArray(updatedTaskData.developers)
              ? updatedTaskData.developers
              : updatedTaskData.developer_ids?.map((id) => ({ id })) || task.developers || [],
          };
          highlightItem(taskWithUpdates.id);
          return taskWithUpdates;
        }
        return task;
      });
    });

    setEditDialogVisible(false);

    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task updated successfully",
      life: 3000,
    });
  };

  // Handle updating task status without refreshing all data
  const handleUpdateTaskStatus = (updatedTaskData) => {
    if (!updatedTaskData) return;

    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          const taskWithUpdates = { ...task, ...updatedTaskData };
          highlightItem(taskWithUpdates.id);
          return taskWithUpdates;
        }
        return task;
      });
    });

    setStatusDialogVisible(false);

    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task status updated successfully",
      life: 3000,
    });
  };

  // Status options for filtering
  const statusOptions = [
    { label: "Active Tasks (excl. Done)", value: "exclude_done" },
    { label: "All Statuses", value: "all" },
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Schedule (planned-date) options for filtering
  const scheduleOptions = [
    { label: "All Tasks", value: "all" },
    { label: "Scheduled", value: "scheduled" },
    { label: "Unscheduled", value: "unscheduled" },
  ];

  // Project options are derived from the loaded tasks rather than fetched from
  // /project: it costs no extra request and only offers projects that actually
  // have tasks, so picking one can never give an empty list. The count shows
  // how many tasks each project holds.
  // Every filter EXCEPT project. The project dropdown counts off this list and
  // the table filters off it too, so "Artisan (2)" always means "selecting
  // Artisan shows 2 rows" under whatever else is currently applied.
  const tasksBeforeProject = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return tasks.filter((task) => {
      const matchesSearch =
        term === "" ||
        task.title?.toLowerCase().includes(term) ||
        (task.description && task.description.toLowerCase().includes(term));

      const matchesStatus =
        statusFilter === "all" ||
        !statusFilter ||
        (statusFilter === "exclude_done" ? task.status !== "Done" : task.status === statusFilter);

      const matchesDeveloper =
        !developerFilter || (task.developers && task.developers.some((dev) => dev.id === developerFilter));

      const matchesSchedule =
        scheduleFilter === "all" || (scheduleFilter === "scheduled" ? !!task.start_date : !task.start_date);

      return matchesSearch && matchesStatus && matchesDeveloper && matchesSchedule;
    });
  }, [tasks, searchTerm, statusFilter, developerFilter, scheduleFilter]);

  const projectOptions = useMemo(() => {
    const counts = new Map();
    tasksBeforeProject.forEach((task) => {
      const project = task.project;
      if (!project?.id) return;
      const entry = counts.get(project.id);
      if (entry) {
        entry.count += 1;
      } else {
        counts.set(project.id, { id: project.id, title: project.title || `Project ${project.id}`, count: 1 });
      }
    });

    const options = Array.from(counts.values())
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((p) => ({ label: `${p.title} (${p.count})`, value: p.id }));

    const unassigned = tasksBeforeProject.filter((task) => !task.project?.id).length;
    if (unassigned > 0) {
      options.push({ label: `No project (${unassigned})`, value: "__none__" });
    }

    // The selected project can drop to zero once another filter narrows things
    // (e.g. it has only Done tasks). Keep it listed as "(0)" so the dropdown
    // shows what's applied instead of going blank.
    if (projectFilter && !options.some((o) => o.value === projectFilter)) {
      const known = tasks.find((t) => t.project?.id === projectFilter)?.project;
      options.push({
        label: projectFilter === "__none__" ? "No project (0)" : `${known?.title || "Selected project"} (0)`,
        value: projectFilter,
      });
    }

    return options;
  }, [tasksBeforeProject, projectFilter, tasks]);

  // Fetch tasks and developers on mount
  useEffect(() => {
    fetchCurrentUser();
    fetchData();
    fetchDevelopers();
  }, []);

  // Load the logged-in user and, for a normal member, default the view to their
  // own tasks (they can still clear/change the developer filter to view others).
  const fetchCurrentUser = async () => {
    try {
      const res = await Http.get("/me");
      const user = res.data?.data || res.data;
      setCurrentUser(user);
      if (!isPrivilegedUser(user) && user?.id) {
        setDeveloperFilter(user.id);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await Http.get("/task");
      if (response.data && response.data.data) {
        setTasks(response.data.data);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setError("Failed to load tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDevelopers = async () => {
    try {
      const res = await Http.get("/staff-department-wise/30");
      const options = [{ label: "All Developers", value: null }];
      if (Array.isArray(res.data)) {
        res.data.forEach((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          const displayName = fullName || dev.name || `ID: ${dev.id}`;
          options.push({ label: displayName, value: dev.id });
        });
      }
      setDeveloperOptions(options);
    } catch (error) {
      console.error("Error fetching developers:", error);
    }
  };

  // Delete task handler
  const confirmDelete = (task) => {
    confirmDialog({
      message: `Are you sure you want to delete task "${task.title}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(task.id),
    });
  };

  const handleDelete = async (taskId) => {
    try {
      await Http.delete(`/task/${taskId}`);
      setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Task deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete task",
        life: 3000,
      });
    }
  };

  // Duplicate task handler — clones the task server-side and adds the new row
  const handleDuplicate = async (task) => {
    try {
      const response = await Http.post(`/task/${task.id}/duplicate`);
      const newTaskData = response.data?.data || response.data;
      if (newTaskData && newTaskData.id) {
        handleAddNewTask(newTaskData);
      }
    } catch (error) {
      console.error("Error duplicating task:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to duplicate task",
        life: 3000,
      });
    }
  };

  // Format date as dd/mm/yyyy by parsing the string directly — avoids timezone shift
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const datePart = dateString.split("T")[0];
    if (!datePart || !datePart.includes("-")) return "N/A";
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  };

  const getUserName = (user) => {
    if (!user) return "N/A";
    return user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || `ID: ${user.id}`;
  };

  // Full date + time for the "created" tooltip. created_at is an ISO string,
  // so it renders in the viewer's own timezone.
  const formatDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return null;
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Initials for the avatar chip — "Ahmed Rasel" → "AR", single word → first 2 chars.
  const getInitials = (user) => {
    const name = getUserName(user);
    if (!name || name === "N/A") return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Deterministic avatar colour so the same person keeps the same chip colour.
  const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#0891b2", "#16a34a", "#ca8a04", "#dc2626"];
  const avatarColor = (user) => {
    const id = Number(user?.id) || 0;
    return AVATAR_COLORS[id % AVATAR_COLORS.length];
  };

  // Developers as compact initials chips; names live in the tooltip so the
  // column stays narrow no matter how many people are assigned.
  const renderDevelopers = (developers) => {
    if (!developers || developers.length === 0) {
      return <span className="pm-muted-dash">—</span>;
    }

    const shown = developers.slice(0, 3);
    const rest = developers.length - shown.length;

    return (
      <div className="pm-avatar-stack" title={developers.map(getUserName).join(", ")}>
        {shown.map((dev) => (
          <span key={dev.id} className="pm-avatar" style={{ backgroundColor: avatarColor(dev) }}>
            {getInitials(dev)}
          </span>
        ))}
        {rest > 0 && <span className="pm-avatar pm-avatar-more">{`+${rest}`}</span>}
      </div>
    );
  };

  // Status pill — small, dot + text, one muted tint per status.
  const STATUS_STYLES = {
    "To Do": { key: "todo" },
    "In Progress": { key: "progress" },
    Review: { key: "review" },
    Done: { key: "done" },
  };

  const statusBodyTemplate = (rowData) => {
    const key = STATUS_STYLES[rowData.status]?.key || "todo";
    return (
      <span className={`pm-status pm-status-${key}`}>
        <span className="pm-status-dot" />
        {rowData.status}
      </span>
    );
  };

  // Main "Task" cell: type icon + title + label chip, with project/story beneath.
  // Replaces the old separate Task Type, Project/Story and Label columns.
  const taskBodyTemplate = (rowData) => {
    const taskType = rowData.task_type;
    const label = rowData.label;
    const project = rowData.project?.title;
    const story = rowData.story?.title;
    const creator = getTaskCreator(rowData);
    const createdShort = formatShort(rowData.created_at);
    const createdOn = rowData.created_at ? formatDateTime(rowData.created_at) : null;

    return (
      <div className="pm-task-cell">
        <span
          className="pm-type-icon"
          title={taskType?.task_type || "No type"}
          style={{
            color: taskType?.color || "#64748b",
            backgroundColor: hexToRgba(taskType?.color || "#64748b", 0.12),
          }}
        >
          <i className={taskType?.icon || "pi pi-circle-fill"} />
        </span>

        <div className="pm-task-main">
          <div className="pm-task-titleline">
            <span className="pm-task-title" title={rowData.title}>
              {rowData.title}
            </span>
            {label && (
              <span
                className="pm-label-chip"
                style={{
                  backgroundColor: hexToRgba(label.color || "#94a3b8", 0.14),
                  color: label.color || "#475569",
                  borderColor: hexToRgba(label.color || "#94a3b8", 0.35),
                }}
              >
                {label.name}
              </span>
            )}
          </div>
          {(project || story || creator.name) && (
            <div className="pm-task-sub">
              {project || "No project"}
              {story && (
                <>
                  <span className="pm-sub-sep">/</span>
                  {story}
                </>
              )}
              {/* Who raised this task, and when — the exact timestamp is in the
                  tooltip so the line stays short. */}
              {creator.name && (
                <>
                  <span className="pm-sub-sep">·</span>
                  <span title={createdOn ? `Created ${createdOn}` : undefined}>
                    by {creator.name}
                    {createdShort && <span className="pm-sub-date"> · {createdShort}</span>}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // #rrggbb (or shorthand) → rgba() so a single stored colour gives both the
  // icon tint and its soft background.
  const hexToRgba = (hex, alpha) => {
    if (!hex) return `rgba(100, 116, 139, ${alpha})`;
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return `rgba(100, 116, 139, ${alpha})`;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Inclusive day count between two date strings (YYYY-MM-DD or ISO).
  const planDays = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(String(start).split("T")[0]);
    const e = new Date(String(end).split("T")[0]);
    if (isNaN(s) || isNaN(e)) return null;
    const days = Math.round((e - s) / 86400000) + 1;
    return days > 0 ? days : null;
  };

  // Hour count between two datetime strings, rounded to 1 decimal.
  const actualHours = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e)) return null;
    const hours = (e - s) / 3600000;
    if (hours <= 0) return null;
    return Number.isInteger(hours) ? hours : Number(hours.toFixed(1));
  };

  // Compact date for the dense timeline cell: "12 Aug" instead of "12/08/2025".
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const formatShort = (dateString) => {
    if (!dateString) return "—";
    const datePart = String(dateString).split("T")[0];
    const [, month, day] = datePart.split("-");
    if (!month || !day) return "—";
    return `${Number(day)} ${MONTHS[Number(month) - 1] || ""}`.trim();
  };

  // Timeline cell — two tight rows, icon-led instead of "Plan:" / "Actual:"
  // labels, so the column carries the same information in half the width.
  const timelineBodyTemplate = (rowData) => {
    const days = planDays(rowData.start_date, rowData.end_date);
    const estimated = rowData.estimated_time;
    const planMeta = [days ? `${days}d` : null, estimated || null].filter(Boolean).join(" · ");

    const planRow =
      !rowData.start_date && !rowData.end_date ? (
        <div className="pm-time-row pm-time-muted">
          <i className="pi pi-calendar" />
          <span>Unscheduled</span>
          {estimated && <span className="pm-time-meta">{estimated}</span>}
        </div>
      ) : (
        <div className="pm-time-row">
          <i className="pi pi-calendar" />
          <span>
            {formatShort(rowData.start_date)} <span className="pm-arrow">→</span> {formatShort(rowData.end_date)}
          </span>
          {planMeta && <span className="pm-time-meta">{planMeta}</span>}
        </div>
      );

    let actualRow;
    if (!rowData.actual_start_date) {
      actualRow = (
        <div className="pm-time-row pm-time-muted">
          <i className="pi pi-clock" />
          <span>Not started</span>
        </div>
      );
    } else if (!rowData.actual_end_date) {
      actualRow = (
        <div className="pm-time-row pm-time-running">
          <i className="pi pi-clock" />
          <span>
            {formatShort(rowData.actual_start_date)} <span className="pm-arrow">→</span> running
          </span>
        </div>
      );
    } else {
      const hours = actualHours(rowData.actual_start_date, rowData.actual_end_date);
      actualRow = (
        <div className="pm-time-row">
          <i className="pi pi-clock" />
          <span>
            {formatShort(rowData.actual_start_date)} <span className="pm-arrow">→</span> {formatShort(rowData.actual_end_date)}
          </span>
          {hours != null && <span className="pm-time-meta">{hours}h</span>}
        </div>
      );
    }

    return (
      <div className="pm-timeline-cell" title={`Plan: ${formatDate(rowData.start_date)} → ${formatDate(rowData.end_date)}`}>
        {planRow}
        {actualRow}
      </div>
    );
  };

  // Open the details modal for a task, fetching the full record (with status
  // history) since the list payload is trimmed.
  const openDetails = async (task) => {
    setDetailsDialogVisible(true);
    setDetailsLoading(true);
    setDetailsTask(null);
    try {
      const response = await Http.get(`/task/${task.id}`);
      setDetailsTask(response.data?.data || null);
    } catch (error) {
      console.error("Error fetching task details:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load task details.",
        life: 3000,
      });
      setDetailsDialogVisible(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Template for actions column. Everyone can open the details modal; admins/heads
  // get every action; a normal member can only change the status of a task
  // assigned to them, and gets no edit/copy/delete on any task.
  const actionsBodyTemplate = (rowData) => {
    const btnStyle = { width: "26px", height: "26px", padding: "0" };
    const canChangeStatus = canManageTask(rowData);

    return (
      // Row click already opens details, so keep button clicks from bubbling.
      <div className="pm-actions" onClick={(e) => e.stopPropagation()}>
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-secondary p-button-sm p-button-text"
          style={btnStyle}
          onClick={() => openDetails(rowData)}
          tooltip="Details"
          tooltipOptions={{ position: "top" }}
        />
        {canEditTask(rowData, currentUser) && (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-success p-button-sm p-button-text"
            style={btnStyle}
            onClick={() => {
              setSelectedTask(rowData);
              setEditDialogVisible(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: "top" }}
          />
        )}
        {privileged && (
          <Button
            icon="pi pi-copy"
            className="p-button-rounded p-button-help p-button-sm p-button-text"
            style={btnStyle}
            onClick={() => handleDuplicate(rowData)}
            tooltip="Duplicate"
            tooltipOptions={{ position: "top" }}
          />
        )}
        {canChangeStatus && (
          <Button
            icon="pi pi-sync"
            className="p-button-rounded p-button-info p-button-sm p-button-text"
            style={btnStyle}
            onClick={() => {
              setSelectedTask(rowData);
              setStatusDialogVisible(true);
            }}
            tooltip="Update Status"
            tooltipOptions={{ position: "top" }}
          />
        )}
        {canDeleteTask(rowData, currentUser) && (
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger p-button-sm p-button-text"
            style={btnStyle}
            onClick={() => confirmDelete(rowData)}
            tooltip="Delete"
            tooltipOptions={{ position: "top" }}
          />
        )}
      </div>
    );
  };

  // Only the project cut is left — everything else already applied above.
  const filteredTasks = useMemo(
    () =>
      tasksBeforeProject.filter(
        (task) =>
          !projectFilter ||
          (projectFilter === "__none__" ? !task.project?.id : task.project?.id === projectFilter)
      ),
    [tasksBeforeProject, projectFilter]
  );

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Tasks </h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center gap-2">
              <span className="p-input-icon-left" style={{ width: "280px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tasks..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
              <Button
                className="p-button-outlined p-button-secondary"
                onClick={(e) => filterPanelRef.current.toggle(e)}
                aria-label="Filters"
              >
                <i className="pi pi-filter me-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge value={activeFilterCount} severity="info" className="ms-2" />
                )}
              </Button>
              <OverlayPanel ref={filterPanelRef} className="task-filter-overlay" style={{ width: "auto" }}>
                <div className="d-flex flex-row align-items-end gap-3">
                  <div style={{ minWidth: "200px" }}>
                    <label className="form-label small mb-1">Project</label>
                    <Dropdown
                      value={projectFilter}
                      options={projectOptions}
                      onChange={(e) => {
                        setProjectFilter(e.value);
                        filterPanelRef.current?.hide();
                      }}
                      placeholder="All projects"
                      className="w-100"
                      scrollHeight="220px"
                      showClear={!!projectFilter}
                      filter={projectOptions.length > 8}
                      filterPlaceholder="Search project..."
                      emptyMessage="No projects"
                    />
                  </div>
                  <div style={{ minWidth: "180px" }}>
                    <label className="form-label small mb-1">Status</label>
                    <Dropdown
                      value={statusFilter}
                      options={statusOptions}
                      onChange={(e) => {
                        setStatusFilter(e.value);
                        filterPanelRef.current?.hide();
                      }}
                      className="w-100"
                      scrollHeight="220px"
                    />
                  </div>
                  <div style={{ minWidth: "160px" }}>
                    <label className="form-label small mb-1">Schedule</label>
                    <Dropdown
                      value={scheduleFilter}
                      options={scheduleOptions}
                      onChange={(e) => {
                        setScheduleFilter(e.value);
                        filterPanelRef.current?.hide();
                      }}
                      className="w-100"
                      scrollHeight="220px"
                    />
                  </div>
                  <div style={{ minWidth: "180px" }}>
                    <label className="form-label small mb-1">Developer</label>
                    <Dropdown
                      value={developerFilter}
                      options={developerOptions}
                      onChange={(e) => {
                        setDeveloperFilter(e.value);
                        filterPanelRef.current?.hide();
                      }}
                      placeholder="All developers"
                      className="w-100"
                      scrollHeight="220px"
                      showClear={!!developerFilter}
                    />
                  </div>
                  <Button
                    label="Clear all"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={() => {
                      clearFilters();
                      filterPanelRef.current?.hide();
                    }}
                    disabled={activeFilterCount === 0}
                  />
                </div>
              </OverlayPanel>
            </div>
            <div className="d-flex gap-2">
              {privileged && (
                <Button
                  label="Upload CSV"
                  icon="pi pi-upload"
                  className="p-button-info"
                  onClick={() => setBulkDialogVisible(true)}
                />
              )}
              {/* Any member can raise their own task; the server files it against
                  them and starts it at "To Do". */}
              <Button label="Create Task" icon="pi pi-plus" className="p-button-success" onClick={() => setCreateDialogVisible(true)} />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <Toast ref={toast} />
            <ConfirmDialog />

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <ProgressSpinner />
                <p className="mt-2">Loading tasks...</p>
              </div>
            ) : (
              <DataTable
                value={filteredTasks}
                paginator
                rows={100}
                rowsPerPageOptions={[100, 150, 250]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} tasks"
                responsiveLayout="scroll"
                emptyMessage="No tasks found"
                sortField="id"
                sortOrder={-1}
                className="pm-task-table"
                stripedRows
                onRowClick={(e) => openDetails(e.data)}
                rowClassName={(rowData) => {
                  const classes = [];
                  if (highlightedItems[`task-${rowData.id}`]) classes.push("highlight-row");
                  // Dim tasks that don't belong to a normal member so it's clear
                  // they're read-only / not theirs.
                  if (!privileged && !canManageTask(rowData)) classes.push("pm-row-inactive");
                  return classes.join(" ");
                }}
              >
                <Column
                  body={taskBodyTemplate}
                  field="title"
                  header="Task"
                  sortable
                  style={{ minWidth: "320px" }}
                />
                <Column body={timelineBodyTemplate} header="Timeline" style={{ width: "210px" }} />
                <Column
                  body={statusBodyTemplate}
                  field="status"
                  header="Status"
                  sortable
                  style={{ width: "130px" }}
                />
                <Column
                  body={(rowData) => renderDevelopers(rowData.developers)}
                  header="Assignees"
                  style={{ width: "120px" }}
                />
                <Column body={actionsBodyTemplate} header="" style={{ width: "150px" }} />
              </DataTable>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog
        header="Upload Tasks from CSV"
        visible={bulkDialogVisible}
        style={{ width: "90vw" }}
        onHide={() => setBulkDialogVisible(false)}
        maximizable
      >
        <BulkUpload setVisible={setBulkDialogVisible} onSuccess={handleBulkCreated} />
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog header="Create Task" visible={createDialogVisible} style={{ width: "80vw" }} onHide={() => setCreateDialogVisible(false)} maximizable>
        <Create
          privileged={privileged}
          setVisible={setCreateDialogVisible}
          onSuccess={(newTaskData) => {
            if (newTaskData && newTaskData.id) {
              handleAddNewTask(newTaskData);
            } else {
              setCreateDialogVisible(false);
            }
          }}
        />
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog header="Edit Task" visible={editDialogVisible} style={{ width: "80vw" }} onHide={() => setEditDialogVisible(false)} maximizable>
        {selectedTask && <Edit taskId={selectedTask.id} privileged={privileged} setVisible={setEditDialogVisible} onSuccess={handleUpdateTask} />}
      </Dialog>

      {/* Task Details Dialog */}
      <Dialog
        header="Task Details"
        visible={detailsDialogVisible}
        style={{ width: "80vw" }}
        onHide={() => setDetailsDialogVisible(false)}
        maximizable
      >
        {detailsLoading ? (
          <div className="text-center p-5">
            <ProgressSpinner />
            <p className="mt-2">Loading task details...</p>
          </div>
        ) : (
          <TaskDetails task={detailsTask} showActions={false} />
        )}
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog header="Update Task Status" visible={statusDialogVisible} style={{ width: "50vw" }} onHide={() => setStatusDialogVisible(false)}>
        {selectedTask && (
          <StatusUpdate
            taskId={selectedTask.id}
            currentStatus={selectedTask.status}
            setVisible={setStatusDialogVisible}
            onSuccess={handleUpdateTaskStatus}
          />
        )}
      </Dialog>
    </div>
  );
};

export default List;
